import React from "react";
import { motion } from "framer-motion";

const formatAppointment = (isoString) => {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleString("en-GB", options).replace(",", "");
};

const PrescriptionPreview = ({
  patientName,
  date,
  symptoms,
  diagnosis,
  medicines,
  labTests,
  sos,
  notes,
  nextAppointment,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-3xl shadow-xl p-6 border border-emerald-100 text-sm sm:text-base transition-all hover:shadow-2xl"
    >
      <h2 className="text-2xl font-bold text-emerald-700 text-center mb-6">
        🩺 Digital Prescription Overview
      </h2>

      {/* Patient Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-gray-700">
        <div>
          <p><span className="font-semibold">👤 Name:</span> {patientName}</p>
          <p><span className="font-semibold">📅 Date:</span> {date}</p>
        </div>
        <div>
          <p><span className="font-semibold">🚨 SOS:</span> {sos || "N/A"}</p>
        </div>
      </div>

      {/* Section Divider */}
      <div className="border-t border-gray-200 mb-6"></div>

      {/* Symptoms */}
      <div className="mb-5">
        <h3 className="font-semibold text-emerald-600 mb-1">🩹 Symptoms</h3>
        <p className="text-gray-800 bg-emerald-50 p-3 rounded-xl">{symptoms || "N/A"}</p>
      </div>

      {/* Diagnosis */}
      <div className="mb-5">
        <h3 className="font-semibold text-emerald-600 mb-1">🧠 Diagnosis</h3>
        <p className="text-gray-800 bg-emerald-50 p-3 rounded-xl">{diagnosis || "N/A"}</p>
      </div>

      {/* Medicines */}
      <div className="mb-6">
        <h3 className="font-semibold text-emerald-600 mb-2">💊 Medicines</h3>
        <div className="overflow-x-auto rounded-lg border border-emerald-200">
          <table className="min-w-full text-sm text-left text-gray-800 bg-white">
            <thead className="bg-emerald-100 text-emerald-900 font-semibold">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Dosage</th>
                <th className="p-3">Time</th>
                <th className="p-3">Days</th>
              </tr>
            </thead>
            <tbody>
              {medicines && medicines.length > 0 ? (
                medicines.map((med, index) => (
                  <tr key={index} className="border-t hover:bg-emerald-50">
                    <td className="p-3">{med.name || "-"}</td>
                    <td className="p-3">{med.dosage || "-"}</td>
                    <td className="p-3">{med.time || "-"}</td>
                    <td className="p-3">{med.days || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center p-3">No medicines prescribed.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lab Tests */}
      <div className="mb-6">
        <h3 className="font-semibold text-emerald-600 mb-1">🧪 Lab Tests</h3>
        <ul className="list-disc list-inside text-gray-800 bg-emerald-50 p-3 rounded-xl">
          {labTests && labTests.filter(Boolean).length > 0 ? (
            labTests.map((test, index) => {
              const parsed = JSON.parse(test.replace(/'/g, '"'));
              return <li key={index}>{parsed.name}</li>;
            })
          ) : (
            <li>No lab tests recommended.</li>
          )}
        </ul>
      </div>

      {/* Notes */}
      {notes && (
        <div className="mb-6">
          <h3 className="font-semibold text-emerald-600 mb-1">📝 Notes</h3>
          <p className="text-gray-800 bg-emerald-50 p-3 rounded-xl">{notes}</p>
        </div>
      )}

      {/* Next Appointment */}
      {nextAppointment && (
        <div className="mb-2">
          <h3 className="font-semibold text-emerald-600 mb-1">📆 Next Appointment</h3>
          <p className="text-gray-800">{formatAppointment(nextAppointment)}</p>
        </div>
      )}
    </motion.div>
  );
};

export default PrescriptionPreview;
